<template>
  <div v-if="model">
    <Card>
      <template #title>{{ $t(verificationStateType) }} </template>
      <template #content>
        <div>
          <h6>{{ $t('verificationState') }}</h6>
        </div>
        <div>
          <b-badge class="verificationState" :variant="verificationStateColor(state)">
            {{ $t(state) }}
          </b-badge>
        </div>
      </template>
      <template #footer v-if="!isGuest && !readonly">
        <hr />
        <b-button
          class="mr-1"
          v-if="showStartVerificationButton"
          @click="onStartVerification()"
          variant="primary"
        >
          {{ $t('start_verification') }}
        </b-button>
        <b-button
          class="mr-1"
          v-if="showRequestVerificationButton"
          @click="onRequestAudit()"
          variant="warning"
        >
          {{ $t('request_verification') }}
        </b-button>
        <b-button
          class="mr-1"
          v-if="showApprovedButton"
          @click="onApproveVerification()"
          variant="success"
        >
          {{ $t('approve_verification') }}
        </b-button>
        <b-button
          class="mr-1"
          v-if="showRejectButton"
          @click="onRejectVerification()"
          variant="danger"
        >
          {{ $t('reject_verification') }}
        </b-button>
        <b-button v-if="showResetButton" @click="onResetVerification()" variant="bg">
          {{ $t('reset') }}
        </b-button>
      </template>
    </Card>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Card from 'primevue/card';
import { verificationStateColor } from '@/helpers/colors';
import { hasResourcePermission } from '@/helpers/userCapabilities';

export default {
  components: { Card },
  props: {
    project: { type: Object, default: null },
    employeeRequest: { type: Object, default: null },
    verificationStateType: { type: String },
    readonly: { type: Boolean, default: false },
  },
  data() {
    return {};
  },
  computed: {
    ...mapGetters(['getEnumValues']),
    ...mapGetters('auth', ['isAdmin', 'getCurrentUser', 'isClient', 'isGuest']),

    // Model could be either project or employeeRequest
    model() {
      return this.project || this.employeeRequest;
    },

    showRequestVerificationButton() {
      if (this.state === 'UNAUDITED' || this.state === 'REJECTED') {
        return true;
      }

      return false;
    },
    showStartVerificationButton() {
      if (this.state === 'REQUESTED' && !this.isOnSiteWorker) {
        return true;
      }

      return false;
    },
    showApprovedButton() {
      if (this.isOnSiteWorker) return false;

      if (this.state === 'IN_PROGRESS') {
        return true;
      }

      return false;
    },
    showRejectButton() {
      if (this.isOnSiteWorker) return false;

      if (this.state === 'IN_PROGRESS') {
        return true;
      }

      return false;
    },
    showResetButton() {
      if (!this.isAdmin) return false;

      if (this.state !== 'UNAUDITED') {
        return true;
      }

      return false;
    },
    isOnSiteWorker() {
      if (this.isAdmin || this.isClient) {
        return false;
      }
      console.log(
        'isOnSiteWorker',
        hasResourcePermission(this.getCurrentUser, 'PROJECT', ['UPDATE'])
      );
      return !hasResourcePermission(this.getCurrentUser, 'PROJECT', ['UPDATE']);
    },
    state() {
      return this.model[this.verificationStateType];
    },
  },

  methods: {
    ...mapActions(['updateProjectVerificationState', 'updateEmployeeRequestVerificationState']),
    async onRequestAudit() {
      if (this.project) {
        await this.updateProjectVerificationState({
          projectNumber: this.project.number,
          verificationStateInput: {
            verificationStateType: this.verificationStateType,
            verificationState: 'REQUESTED',
          },
        });
      } else if (this.employeeRequest) {
        await this.updateEmployeeRequestVerificationState({
          employeeRequestNumber: this.employeeRequest.number,
          verificationStateInput: {
            verificationStateType: this.verificationStateType,
            verificationState: 'REQUESTED',
          },
        });
      }
    },
    async onStartVerification() {
      if (this.project) {
        await this.updateProjectVerificationState({
          projectNumber: this.project.number,
          verificationStateInput: {
            verificationStateType: this.verificationStateType,
            verificationState: 'IN_PROGRESS',
          },
        });
      } else if (this.employeeRequest) {
        await this.updateEmployeeRequestVerificationState({
          employeeRequestNumber: this.employeeRequest.number,
          verificationStateInput: {
            verificationStateType: this.verificationStateType,
            verificationState: 'IN_PROGRESS',
          },
        });
      }
    },
    async onApproveVerification() {
      if (this.project) {
        await this.updateProjectVerificationState({
          projectNumber: this.project.number,
          verificationStateInput: {
            verificationStateType: this.verificationStateType,
            verificationState: 'APPROVED',
          },
        });
      } else if (this.employeeRequest) {
        await this.updateEmployeeRequestVerificationState({
          employeeRequestNumber: this.employeeRequest.number,
          verificationStateInput: {
            verificationStateType: this.verificationStateType,
            verificationState: 'APPROVED',
          },
        });
      }
    },
    async onRejectVerification() {
      if (this.project) {
        await this.updateProjectVerificationState({
          projectNumber: this.project.number,
          verificationStateInput: {
            verificationStateType: this.verificationStateType,
            verificationState: 'REJECTED',
          },
        });
      } else if (this.employeeRequest) {
        await this.updateEmployeeRequestVerificationState({
          employeeRequestNumber: this.employeeRequest.number,
          verificationStateInput: {
            verificationStateType: this.verificationStateType,
            verificationState: 'REJECTED',
          },
        });
      }
    },
    async onResetVerification() {
      if (this.project) {
        await this.updateProjectVerificationState({
          projectNumber: this.project.number,
          verificationStateInput: {
            verificationStateType: this.verificationStateType,
            verificationState: 'UNAUDITED',
          },
        });
      } else if (this.employeeRequest) {
        await this.updateEmployeeRequestVerificationState({
          employeeRequestNumber: this.employeeRequest.number,
          verificationStateInput: {
            verificationStateType: this.verificationStateType,
            verificationState: 'UNAUDITED',
          },
        });
      }
    },
    verificationStateColor,
  },
};
</script>

<style scoped lang="scss">
.badge.verificationState {
  padding: 0.6em;
  font-size: 1em;
}

::v-deep .p-card .p-card-footer {
  min-height: 45px;
  padding-top: 0;
}

::v-deep .p-card .p-card-content {
  padding-top: 0.3em;
}
</style>
